import React from 'react';
import Navbar from '../Navbar';
import Footer from '../Footer';
import GuestChatWidget from '../GuestChatWidget';
import '../landing.scss';
import SeoHead from '../../../../Components/Common/SeoHead';

const APP_URL = process.env.REACT_APP_APP_URL || '';

const DIFFS = [
  { icon: 'ri-coin-line', title: '3x cheaper', body: 'Coderom from $14.90/mo. Easync from $49.99/mo for the same core features.' },
  { icon: 'ri-robot-line', title: 'Auto-ordering included', body: 'Both include AO. But Coderom costs 3x less for the same automation power.' },
  { icon: 'ri-calendar-check-line', title: '14-day free trial, no CC', body: 'Try free, no credit card. Easync offers only a 7-day trial.' },
  { icon: 'ri-flag-line', title: 'UK eBay focused', body: 'Coderom is purpose-built for Amazon→eBay UK. Easync is US-first.' },
  { icon: 'ri-shield-check-line', title: 'VeRO brand protection', body: '128 VeRO brands detected and cleaned automatically on every listing.' },
  { icon: 'ri-pulse-line', title: 'Live Amazon stock sync', body: 'Real-time monitoring prevents overselling when Amazon stock changes.' },
];

const TABLE = [
  { feature: 'Starting price', coderom: '$14.90/mo', comp: '$49.99/mo' },
  { feature: 'Free trial', coderom: '14 days, no CC', comp: '7 days' },
  { feature: 'Auto-ordering', coderom: '✓ All plans', comp: '✓ All plans' },
  { feature: 'AI title optimization', coderom: '✓ Advanced+', comp: '✗' },
  { feature: 'Safe repricing', coderom: '✓ All plans', comp: 'Basic' },
  { feature: 'Amazon UK focus', coderom: '✓ Purpose-built', comp: 'US-first' },
  { feature: 'eBay marketplaces', coderom: '8 eBay markets', comp: 'Limited' },
  { feature: 'Gmail integration', coderom: '✓ All plans', comp: '✗' },
  { feature: 'Store health monitoring', coderom: '✓ All plans', comp: '✗' },
  { feature: 'Shopify/multi-platform', coderom: '✗ eBay only', comp: '✗ eBay focused' },
];

const FAQ = [
  { q: 'Is Coderom cheaper than Easync?', a: "Yes. Coderom starts at $14.90/month. Easync's entry plan is $49.99/month — over 3x more expensive." },
  { q: 'Does Easync have AI title optimization?', a: 'No. Easync focuses on repricing and order fulfillment. Coderom includes AI title optimization on Advanced+ plans.' },
  { q: 'Which free trial is better?', a: 'Coderom offers 14 days free, no credit card. Easync offers 7 days.' },
  { q: 'Does Easync work for UK eBay sellers?', a: 'Easync supports eBay but is primarily US-focused. Coderom is purpose-built for UK, US, DE and AU.' },
  { q: 'What makes Coderom different from Easync?', a: 'Lower price, AI features, VeRO protection, UK focus, and a 14-day no-CC trial.' },
];

const renderCell = (text: string): React.ReactNode => {
  if (text.startsWith('✓')) return <><span className="check">✓</span>{text.slice(1)}</>;
  if (text.startsWith('✗')) return <><span className="cross">✗</span>{text.slice(1)}</>;
  return text;
};

const CompareEasync: React.FC = () => {
  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="Coderom vs Easync 2026: Amazon→eBay Tool Comparison"
        description="Compare Coderom vs Easync. Coderom starts at $14.90/mo. Easync starts at $49.99/mo. Both include auto-ordering. Coderom offers 14-day free trial, no credit card."
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQ.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        }) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.coderom.com/" },
            { "@type": "ListItem", "position": 2, "name": "Coderom vs Easync", "item": "https://www.coderom.com/compare/coderom-vs-easync" },
          ],
        }) }}
      />
      <Navbar />

      <div className="compare-page">
        {/* HERO */}
        <div className="compare-hero">
          <div className="compare-hero-bg" />
          <div className="compare-hero-content">
            <div className="hero-breadcrumb">
              <a href="/">Home</a>
              <span>›</span>
              <span>Compare</span>
              <span>›</span>
              <span>Coderom vs Easync</span>
            </div>
            <div className="compare-eyebrow">
              <i className="ri-exchange-line" aria-hidden="true"></i>
              2026 Comparison
            </div>
            <h1>A more <span>affordable</span> alternative<br />to Easync</h1>
            <p>Same auto-ordering power, half the price. Purpose-built for Amazon→eBay dropshipping.</p>
            <div className="compare-chips">
              <span className="compare-chip"><i className="ri-check-line" aria-hidden="true"></i>Updated June 2026</span>
              <span className="compare-chip"><i className="ri-check-line" aria-hidden="true"></i>Pricing verified</span>
              <span className="compare-chip"><i className="ri-check-line" aria-hidden="true"></i>No CC required</span>
            </div>
            <a href={`${APP_URL}/register`} className="compare-cta-btn">Start Free Trial →</a>
          </div>
        </div>

        <div className="compare-wrapper">
          {/* DIFF GRID */}
          <h2 className="section-heading">Why sellers choose Coderom over Easync</h2>
          <div className="diff-grid">
            {DIFFS.map((d, i) => (
              <div className="diff-card" key={i}>
                <div className="diff-icon"><i className={d.icon} aria-hidden="true"></i></div>
                <h3>{d.title}</h3>
                <p>{d.body}</p>
              </div>
            ))}
          </div>

          {/* COMPARISON TABLE */}
          <h2 className="section-heading">Coderom vs Easync — feature comparison</h2>
          <div className="compare-table-wrap">
            <table className="compare-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th className="col-coderom">Coderom <span className="winner-badge">Winner</span></th>
                  <th>Easync</th>
                </tr>
              </thead>
              <tbody>
                {TABLE.map((row, i) => (
                  <tr key={i}>
                    <td>{row.feature}</td>
                    <td className="col-coderom">{renderCell(row.coderom)}</td>
                    <td>{renderCell(row.comp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="compare-disclaimer">* Prices verified June 2026. Verify current Easync pricing at easync.io.</p>

          {/* FAQ */}
          <h2 className="section-heading">Frequently asked questions</h2>
          <div className="compare-faq">
            {FAQ.map((f, i) => (
              <div className="compare-faq-item" key={i}>
                <div className="compare-faq-q">{f.q}</div>
                <div className="compare-faq-a">{f.a}</div>
              </div>
            ))}
          </div>

          {/* RELATED LINKS */}
          <section className="compare-related">
            <h2 className="section-heading">Keep exploring</h2>
            <div className="related-links-grid">
              <a href="/blog/best-ebay-dropshipping-software" className="related-link-card">
                <span className="rl-label">Guide</span>
                <span className="rl-title">Best eBay Dropshipping Software 2026</span>
              </a>
              <a href="/compare/coderom-vs-autods" className="related-link-card">
                <span className="rl-label">Compare</span>
                <span className="rl-title">Coderom vs AutoDS</span>
              </a>
              <a href="/compare/coderom-vs-yaballe" className="related-link-card">
                <span className="rl-label">Compare</span>
                <span className="rl-title">Coderom vs Yaballe</span>
              </a>
              <a href="/tools/ebay-fee-calculator" className="related-link-card">
                <span className="rl-label">Free Tool</span>
                <span className="rl-title">eBay Fee Calculator</span>
              </a>
            </div>
          </section>

          {/* CTA */}
          <div className="compare-cta-section">
            <h2>Half the price, same power.</h2>
            <p>Start your 14-day free trial — no credit card required.</p>
            <a href={`${APP_URL}/register`} className="cta-btn">Start Free Trial →</a>
            <div className="compare-cta-sub">No credit card required · Cancel anytime</div>
          </div>
        </div>
      </div>

      <Footer />
      <GuestChatWidget />

      <style>{`
        .hero-breadcrumb { font-size: 12px; color: #94a3b8; margin-bottom: 12px; display: flex; gap: 6px; justify-content: center; flex-wrap: wrap; }
        .hero-breadcrumb a { color: #cbd5e1; text-decoration: none; }
        .hero-breadcrumb a:hover { color: #fb923c; }
        .compare-related { margin-bottom: 2rem; }
        .related-links-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; }
        .related-link-card { display: flex; flex-direction: column; gap: 4px; padding: 14px 16px; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; text-decoration: none; transition: border-color 0.15s; }
        .related-link-card:hover { border-color: #f97316; }
        .rl-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #f97316; }
        .rl-title { font-size: 13px; font-weight: 500; color: #1e293b; line-height: 1.4; }
        .compare-page { background: #f8fafc; min-height: 100vh; }
        .compare-hero {
          position: relative; overflow: hidden;
          border-radius: 0;
          padding: 80px 0 60px;
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 2rem;
        }
        .compare-hero-bg {
          position: absolute; inset: 0;
          background:
            linear-gradient(135deg,
              rgba(15,23,42,0.72) 0%,
              rgba(15,23,42,0.55) 100%
            ),
            url('/images/tools/hero-compare.jpg')
            center/cover no-repeat;
        }
        .compare-hero-content {
          position: relative; z-index: 1;
          padding: 3rem 2rem; max-width: 720px;
          text-align: center;
          width: 100%;
        }
        .compare-eyebrow {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 4px 14px;
          background: rgba(249,115,22,0.2);
          border: 1px solid rgba(249,115,22,0.4);
          border-radius: 999px; font-size: 12px;
          font-weight: 600; color: #fb923c;
          margin-bottom: 1rem;
          text-transform: uppercase; letter-spacing: 0.06em;
        }
        .compare-hero h1 {
          font-size: 2.2rem; font-weight: 700; color: #fff;
          line-height: 1.3; margin-bottom: 0.75rem;
        }
        .compare-hero h1 span { color: #fb923c; }
        .compare-hero p {
          font-size: 1rem; color: #cbd5e1;
          max-width: 540px; margin-bottom: 1.5rem; line-height: 1.6;
        }
        .compare-chips {
          display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 1.5rem; justify-content: center;
        }
        .compare-chip {
          padding: 4px 12px; border-radius: 999px;
          background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.2);
          font-size: 12px; font-weight: 500; color: #e2e8f0;
          display: flex; align-items: center; gap: 4px;
        }
        .compare-chip i { font-size: 11px; color: #fb923c; }
        .compare-cta-btn {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 12px 28px; background: #f97316; color: #fff;
          border: none; border-radius: 10px;
          font-size: 14px; font-weight: 600;
          cursor: pointer; text-decoration: none;
        }
        .compare-wrapper {
          max-width: 900px; margin: 0 auto; padding: 0 1rem 3rem;
        }
        .section-heading {
          font-size: 1.4rem; font-weight: 700;
          color: #1e293b; margin-bottom: 1.25rem;
        }
        .diff-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px; margin-bottom: 2rem;
        }
        .diff-card {
          background: #fff7ed; border: 1px solid #fed7aa;
          border-radius: 12px; padding: 1rem 1.1rem;
        }
        .diff-card .diff-icon {
          width: 36px; height: 36px; border-radius: 8px;
          background: #f97316;
          display: flex; align-items: center;
          justify-content: center; margin-bottom: 10px;
        }
        .diff-card .diff-icon i { font-size: 18px; color: #fff; }
        .diff-card h3 {
          font-size: 14px; font-weight: 600;
          color: #c2410c; margin-bottom: 5px;
        }
        .diff-card p { font-size: 12px; color: #78350f; line-height: 1.6; }
        .compare-table-wrap {
          overflow-x: auto; margin-bottom: 0.75rem;
        }
        .compare-table {
          width: 100%; border-collapse: collapse; font-size: 14px;
        }
        .compare-table thead tr { background: #fff7ed; }
        .compare-table th {
          padding: 12px 16px; text-align: left;
          font-weight: 600; color: #7c2d12;
          border-bottom: 2px solid #fed7aa; white-space: nowrap;
        }
        .compare-table th.col-coderom { color: #f97316; }
        .compare-table td {
          padding: 11px 16px;
          border-bottom: 0.5px solid #e2e8f0;
          color: #1e293b; vertical-align: middle;
        }
        .compare-table tr:last-child td { border-bottom: none; }
        .compare-table tr:hover td { background: #f8fafc; }
        .compare-table .col-coderom { background: #fff7ed !important; }
        .winner-badge {
          display: inline-block; background: #f97316; color: #fff;
          font-size: 10px; font-weight: 700;
          padding: 1px 8px; border-radius: 999px; margin-left: 6px;
        }
        .check { color: #16a34a; font-weight: 700; }
        .cross { color: #dc2626; }
        .partial { color: #f97316; font-size: 13px; font-weight: 500; }
        .compare-disclaimer {
          font-size: 12px; color: #94a3b8; margin-bottom: 2rem;
        }
        .compare-faq { margin-bottom: 2rem; }
        .compare-faq-item {
          border-bottom: 0.5px solid #e2e8f0; padding: 14px 0;
        }
        .compare-faq-item:last-child { border-bottom: none; }
        .compare-faq-q {
          font-size: 14px; font-weight: 600;
          color: #1e293b; margin-bottom: 6px;
        }
        .compare-faq-a { font-size: 13px; color: #64748b; line-height: 1.6; }
        .compare-cta-section {
          background: #1e293b; border-radius: 16px;
          padding: 2.5rem 2rem; text-align: center;
        }
        .compare-cta-section h2 {
          font-size: 1.5rem; font-weight: 700;
          color: #fff; margin-bottom: 0.5rem;
        }
        .compare-cta-section p {
          font-size: 14px; color: #94a3b8; margin-bottom: 1.25rem;
        }
        .compare-cta-section .cta-btn {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 12px 28px; background: #f97316; color: #fff;
          border: none; border-radius: 10px;
          font-size: 14px; font-weight: 600; cursor: pointer;
          text-decoration: none;
        }
        .compare-cta-sub {
          font-size: 12px; color: #64748b; margin-top: 10px;
        }
        @media (max-width: 768px) {
          .diff-grid { grid-template-columns: 1fr; }
          .compare-hero h1 { font-size: 1.7rem; }
        }
      `}</style>
    </div>
  );
};

export default CompareEasync;
